import React, { useEffect, useState, useRef } from "react";
import PopupModal from "./Ui_elements/PopupModal";
import ChatWindow from "./Ui_elements/ChatWindow";
import { Grid, Box, Typography, Tabs, Tab, IconButton } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useDispatch, useSelector } from "react-redux";
import DropdownComponent from "./DropdownComponent";
import {
  discussionJobDetails,
  discussionScoop,
  discussionScoopDetails,
  getGeneralMasterDropDownList,
  itemsScoopsDropdown,
  selectJobDataByOrderIdDropdown,
  updateDashboardCommentStatus,
  updateReadUnreadChat,
} from "../redux/Thunk/DashboardModule/DashboardThunk";
import Loader from "./Ui_elements/Loader";
import { usersList } from "../redux/Thunk/InvoiceModule/InvoiceThunk";
import { localizeNumber } from "../Helper/generalhelper";

function JobdiscussionComponent({
  openJobDisucussion,
  handleOpenJobDiscussionPage,
  jobData,
  idParams,
  fromScoopHeader = false,
  openFromJob = false,
  showFromFreelance = false,
}) {
  const { user } = useSelector(state => state.root.authUser);

  // By default dropdown options will be selected which will be getting 1st from options.
  // fromScoopHeader will make it open from scoop header and dropdown options will be selected based on user ponumber.
  // openFromJob will make it open from external tab as first and dropdown options will be selected based on user ponumber.
  // showFromFreelance will make it open from freelance screen where dropdown and tabs are not be shown, only external tab is selected by default.

  const dispatch = useDispatch();
  const orderId = jobData?.orderId || jobData?.order_id || idParams;

  const {
    chat_details,
    chat_details_user,
    chat_details_user_job,
    scoop_dropdown_inchat,
    job_dropdown_inchat,
  } = useSelector(state => state.root.DashboardModule);

  const { loading } = useSelector(state => state.root.loading);
  const [open, setOpen] = useState(false);
  const [tabValue, setTabValue] = useState(openFromJob ? 1 : 0);
  const [formData, setFormData] = useState({ scoop: "" });
  const [user_details_on_sidebar, setUser_details_on_sidebar] = useState({});
  const dropdownInitialized = useRef(false);
  const initialized = useRef(false);
  // For freelance mode, we force external tab behavior
  const effectiveTabValue = showFromFreelance ? 1 : tabValue;
  const isInternal = effectiveTabValue === 0;
  const readMarkedRef = useRef(null);
  // True while data for the current tab/open cycle is still being fetched.
  // Prevents stale Redux data from flashing before the loader appears.
  const [isInitializing, setIsInitializing] = useState(true);

  useEffect(() => {
    if (showFromFreelance) {
      dispatch(getGeneralMasterDropDownList({  internalUser: true }));
    }else{
      dispatch(usersList());
    }
  }, []);

  // Memoize dropdown options
  const scoopOptions =
    scoop_dropdown_inchat?.map(item => ({
      label: `${item.order_no} - ${item.item_number.toString().padStart(3, "0")}`,
      value: item.itemId,
    })) || [];

  const jobOptions =
    job_dropdown_inchat?.map(item => ({
      label: item.po_number,
      value: item.job_summmeryId,
    })) || [];

  const dropdownOptions = isInternal ? scoopOptions : jobOptions;

  useEffect(() => {
    if (!showFromFreelance) {
      setOpen(openJobDisucussion);
    }
    if (openJobDisucussion || showFromFreelance) {
      initialized.current = false;
      dropdownInitialized.current = false;
      readMarkedRef.current = null;
      // Show loader immediately so stale Redux data never flashes to the user.
      setIsInitializing(true);
      // Reset tab to the correct position in the same batch as the open flag so
      // the loading effect below fires exactly once with the right effectiveTabValue.
      if (!showFromFreelance) {
        setTabValue(openFromJob ? 1 : 0);
      }
    }
  }, [openJobDisucussion, openFromJob, showFromFreelance]);

  useEffect(() => {
    if ((!open && !showFromFreelance) || dropdownInitialized.current) return;

    // Lock immediately – before any async work – so a second render that
    // changes an unrelated dep cannot start a concurrent loadData call.
    dropdownInitialized.current = true;

    const loadData = async () => {
      const markReadForThread = (type, threadId) => {
        if (!threadId || !user?.iUserId) return;

        const key = `${type}-${threadId}`;
        if (readMarkedRef.current === key) return;
        readMarkedRef.current = key;

        if (type === 1) {
          dispatch(
            updateDashboardCommentStatus({
              id: 0,
              read_id: String(user.iUserId),
              isLinguist: false,
              is_updateByid: 1,
              scoop_id: threadId,
              is_scoopUpdate: true,
              loginUserType: "1",
              markAsRead: true,
            })
          );
          return;
        }

        // External/job chat
        dispatch(
          updateReadUnreadChat({
            id: 0,
            read_id: String(user.iUserId),
            isLinguist: true,
            job_id: threadId,
            is_updateByid: 1,
            loginUserType: showFromFreelance ? "2" : "1",
            markAsRead: true,
          })
        );
      };

      if (showFromFreelance) {
        // Freelance mode - directly use job_summmeryId and external tab
        const job_summmeryId = jobData?.job_summmeryId;
        if (job_summmeryId) {
          setFormData({ scoop: job_summmeryId });
          await dispatch(discussionScoop({ id: job_summmeryId, type: 2 }));
          dispatch(discussionJobDetails(job_summmeryId));
          markReadForThread(2, job_summmeryId);
        }
      } else {
        // Normal behavior
        if (isInternal) {
          const result = await dispatch(itemsScoopsDropdown(orderId));
          if (result?.payload?.length > 0) {
            const defaultId =
              fromScoopHeader && jobData?.itemId ? jobData.itemId : result.payload[0]?.itemId;

            setFormData({ scoop: defaultId });
            await dispatch(discussionScoop({ id: defaultId, type: 1 }));
            dispatch(discussionScoopDetails(defaultId));
            markReadForThread(1, defaultId);
          }
        } else {
          const result = await dispatch(selectJobDataByOrderIdDropdown(orderId));
          if (result?.payload?.length > 0) {
            const defaultId =
              openFromJob && jobData?.job_summmeryId
                ? jobData.job_summmeryId
                : result.payload[0]?.job_summmeryId;
            setFormData({ scoop: defaultId });
            await dispatch(discussionScoop({ id: defaultId, type: 2 }));
            dispatch(discussionJobDetails(defaultId));
            markReadForThread(2, defaultId);
          }
        }
      }

      initialized.current = true;
      setIsInitializing(false);
    };

    loadData().catch(() => {
      // Allow retry on unexpected failure.
      dropdownInitialized.current = false;
      setIsInitializing(false);
    });
  }, [
    effectiveTabValue,
    open,
    orderId,
    fromScoopHeader,
    openFromJob,
    showFromFreelance,
    jobData?.itemId,
    jobData?.job_summmeryId,
    dispatch,
  ]);

  // useEffect(() => {
  //     if (!formData.scoop || !initialized.current) return;

  //     const type = isInternal ? 1 : 2;
  //     const payload = { id: formData.scoop, type };

  //     dispatch(discussionScoop(payload));
  //     if (isInternal) {
  //         if (!fromScoopHeader) dispatch(itemsScoopsDropdown(orderId));
  //         dispatch(discussionScoopDetails(formData.scoop));
  //     } else {
  //         if (!showFromFreelance) {
  //             dispatch(selectJobDataByOrderIdDropdown(orderId));
  //         }
  //         dispatch(discussionJobDetails(formData.scoop));
  //     }
  // }, [formData.scoop, isInternal, fromScoopHeader, orderId, dispatch]);

  useEffect(() => {
    const userData = isInternal ? chat_details_user?.data : chat_details_user_job?.data;
    if (userData) setUser_details_on_sidebar(userData);
  }, [tabValue, chat_details_user, chat_details_user_job, isInternal]);

  // const handleInputChange = (e) => {
  //     const { name, value } = e.target;
  //     setFormData(prev => ({ ...prev, [name]: value }));
  // };
  const handleInputChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    const type = isInternal ? 1 : 2;
    const payload = { id: value, type };

    const maybeMarkRead = async () => {
      await dispatch(discussionScoop(payload));

      if (!value || !user?.iUserId) return;
      const key = `${type}-${value}`;
      if (readMarkedRef.current === key) return;
      readMarkedRef.current = key;

      if (type === 1) {
        dispatch(
          updateDashboardCommentStatus({
            id: 0,
            read_id: String(user.iUserId),
            isLinguist: false,
            is_updateByid: 1,
            scoop_id: value,
            is_scoopUpdate: true,
            loginUserType: "1",
            markAsRead: true,
          })
        );
      } else {
        dispatch(
          updateReadUnreadChat({
            id: 0,
            read_id: String(user.iUserId),
            isLinguist: true,
            job_id: value,
            is_updateByid: 1,
            loginUserType: "1",
            markAsRead: true,
          })
        );
      }
    };
    maybeMarkRead();
    if (isInternal) {
      dispatch(discussionScoopDetails(value));
    } else {
      dispatch(discussionJobDetails(value));
    }
  };

  const handleTabChange = (_, newValue) => {
    setTabValue(newValue);
    setIsInitializing(true);
    dropdownInitialized.current = false;
    initialized.current = false;
  };

  const handleRefresh = () => {
    if (!formData.scoop) return;
    const type = showFromFreelance ? 2 : isInternal ? 1 : 2;
    const payload = { id: formData.scoop, type };

    const maybeMarkRead = async () => {
      await dispatch(discussionScoop(payload));

      if (!formData.scoop || !user?.iUserId) return;
      const key = `${type}-${formData.scoop}`;
      if (readMarkedRef.current === key) return;
      readMarkedRef.current = key;

      if (type === 1) {
        dispatch(
          updateDashboardCommentStatus({
            id: 0,
            read_id: String(user.iUserId),
            isLinguist: false,
            is_updateByid: 1,
            scoop_id: formData.scoop,
            is_scoopUpdate: true,
            loginUserType: "1",
            markAsRead: true,
          })
        );
      } else {
        // In freelance mode we keep existing behavior (loginUserType=2, isLinguist=true),
        // otherwise dashboard external uses loginUserType=1.
        dispatch(
          updateReadUnreadChat({
            id: 0,
            read_id: String(user.iUserId),
            isLinguist: true,
            job_id: formData.scoop,
            is_updateByid: 1,
            loginUserType: showFromFreelance ? "2" : "1",
            markAsRead: true,
          })
        );
      }
    };
    maybeMarkRead();
    if (showFromFreelance || !isInternal) {
      dispatch(discussionJobDetails(formData.scoop));
    } else {
      dispatch(discussionScoopDetails(formData.scoop));
    }
  };

  useEffect(() => {
    if (!showFromFreelance) return;
    if (!formData?.scoop) return;
    if (!user?.iUserId) return;

    // Prevent repeated API calls for same job
    if (readMarkedRef.current === formData.scoop) return;

    readMarkedRef.current = formData.scoop;

    dispatch(
      updateReadUnreadChat({
        id: 0,
        read_id: String(user.iUserId),
        isLinguist: true,
        job_id: formData.scoop,
        is_updateByid: 1,
        loginUserType: "2",
      })
    );

  }, [formData?.scoop, showFromFreelance]);

  const noOptionsAvailable = dropdownOptions.length === 0;

  const renderContent = () => {
    if (loading || isInitializing) {
      return (
        <Box
          sx={{
            height: "425px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#f6f6fc",
            borderRadius: 2,
          }}
        >
          <Loader />
        </Box>
      );
    }

    if (noOptionsAvailable && !showFromFreelance) {
      return (
        <Box
          sx={{
            height: "425px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "#f6f6fc",
            borderRadius: 2,
          }}
        >
          <Typography variant="h6" color="textSecondary">
            {isInternal ? "No items available for discussion" : "No jobs available for discussion"}
          </Typography>
        </Box>
      );
    }

    return (
      <Grid container spacing={2}>
        <Grid item xs={8}>
          <Box
            sx={{
              height: "425px",
              borderRadius: 2,
              backgroundColor: "#f6f6fc",
              display: "flex",
              flexDirection: "column",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <ChatWindow
              data={chat_details}
              userDetails={user_details_on_sidebar}
              selectedJobOrItemIdConditiionally={formData.scoop}
              tabValue={effectiveTabValue}
              showFromFreelance={showFromFreelance}
            />
          </Box>
        </Grid>
        <Grid item xs={4}>
          <Box sx={{ borderLeft: "1px solid #ccc", pl: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ textDecoration: "underline" }}>
              Discussion Board
            </Typography>
            {showFromFreelance ? (
              <>
                <Box mt={2} display="flex" alignItems="center" gap={1}>
                  <Typography variant="body2" fontWeight="bold">
                    Job:
                  </Typography>
                  <Typography variant="body2">{jobData?.poNumber}</Typography>
                </Box>
              </>
            ) : (
              <>
                <Box mt={2}>
                  <Typography variant="body2" fontWeight="bold">
                    Project:
                  </Typography>
                  <Typography variant="body2">{user_details_on_sidebar?.order_no}</Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={1} mt={2}>
                  <DropdownComponent
                    label={isInternal ? "Scoop" : "Job"}
                    name="scoop"
                    value={formData?.scoop}
                    onChange={handleInputChange}
                    options={isInternal ? scoopOptions : jobOptions}
                    isNotMandatory={true}
                    clearIcon={null}
                  />
                </Box>
              </>
            )}
            <Box mt={2}>
              <Typography variant="body2" fontWeight="bold" sx={{ textDecoration: "underline" }}>
                Project Members
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                <strong>Project Manager:</strong> {user_details_on_sidebar?.pm_fullName}
              </Typography>
              <Typography variant="body2">
                <strong>Project Coordinator:</strong> {user_details_on_sidebar?.pc_fullName}
              </Typography>
              <Typography variant="body2">
                <strong>QA Specialist:</strong> {user_details_on_sidebar?.qa_fullName}
              </Typography>
              {!showFromFreelance ? (
                <Typography variant="body2">
                  <strong>Linguist:</strong>{" "}
                  {user_details_on_sidebar?.linguistList?.map(l => l.resource_fullName).join(", ")}
                </Typography>
              ) : (
                <Typography variant="body2">
                  <strong>Linguist:</strong> {(user?.vFirstName + " " + user?.vLastName).trim()}
                </Typography>
              )}
            </Box>
            <Box mt={2}>
              {!showFromFreelance && (
                <Typography variant="body2">
                  <strong>Job Type:</strong> {user_details_on_sidebar?.project_type_code}
                </Typography>
              )}
              <Typography variant="body2">
                <strong>{tabValue === 1 || showFromFreelance ? "Job" : "Scoop"} Price:</strong>{" "}
                {localizeNumber(showFromFreelance ? user_details_on_sidebar?.job_price : user_details_on_sidebar?.total_price)}{" "}
                {showFromFreelance? user_details_on_sidebar?.linguist_currency : user_details_on_sidebar?.client_currency?.split(",")[0]}
              </Typography>
            </Box>
          </Box>
        </Grid>
      </Grid>
    );
  };

  if (showFromFreelance) {
    return (
      <Box
        sx={{
          p: 2,
          backgroundColor: "#fff",
          borderRadius: 2,
          boxShadow: 1,
        }}
      >
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <Typography variant="h6">Discussion Board</Typography>
          <IconButton onClick={handleRefresh}>
            <RefreshIcon />
          </IconButton>
        </Box>
        {renderContent()}
      </Box>
    );
  }

  return (
    <PopupModal
      maxWidth="lg"
      fullWidth
      open={open}
      handleClose={() => {
        setOpen(false);
        handleOpenJobDiscussionPage(false);
      }}
      title={
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="h6">Discussion Board</Typography>
          <IconButton onClick={handleRefresh}>
            <RefreshIcon />
          </IconButton>
          <Tabs value={tabValue} onChange={handleTabChange} sx={{ mt: 0.5 }}>
            <Tab label="Internal" />
            <Tab label="External" />
          </Tabs>
        </Box>
      }
    >
      {renderContent()}
    </PopupModal>
  );
}

export default JobdiscussionComponent;
